import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ISODate } from '@app/shared'
import clsx from 'clsx'

import type { MailTriage } from '../../lib/data/types'
import { MobileSheet } from '../shell/MobileSheet'
import { lastMailCheck, mailRows, sinceWhen, type MailRow } from './day'
import { Glyph } from './glyphs'

// ============================================================================
// BOARD 23 · IMPORTANT MAIL — the mail LifeOS sorted as "needs you today", and nothing else.
//
// LIFEOS HOLDS NO MAIL BODIES (owner, 2026-10-02): what opens under a row is Gmail's own short
// preview, the one piece of the message the sync keeps. The row's "why" is the triage's own reason
// sentence, and "Open in Gmail" is the thread itself.
//
// MARK HANDLED is the Day page's own write (`state: done`, the same endpoint routes/Day.tsx uses):
// the row collapses, the toast offers Undo, and the mail stays in Gmail untouched — LifeOS never
// writes to Gmail.
// ============================================================================

export function MailSheet({
  open,
  onClose,
  today,
  triage,
  handled,
  onHandle,
  onUndoAll,
  onRemind,
}: {
  open: boolean
  onClose: () => void
  today: ISODate
  triage: MailTriage | undefined
  /** Marked handled while this sheet has been open (and not undone). */
  handled: ReadonlySet<string>
  onHandle: (row: MailRow) => void
  onUndoAll: () => void
  onRemind: (row: MailRow) => void
}) {
  const rows = triage ? mailRows(triage, today) : []
  const [expanded, setExpanded] = useState<string | null>(rows[0]?.id ?? null)
  // Each opening starts with the first message open, as board 23 draws it.
  useEffect(() => {
    if (open) setExpanded(rows.find((r) => !handled.has(r.id))?.id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const left = rows.filter((r) => !handled.has(r.id)).length
  const more = triage?.counts.important ?? 0
  const checked = triage ? lastMailCheck(triage) : null
  const unsorted = triage?.counts.unsorted ?? 0

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      label="Important mail"
      panelStyle={{
        height: 'calc(100% - 118px)',
        maxHeight: 'none',
        borderRadius: '34px 34px 0 0',
        boxShadow: 'var(--m-shadow-sheet)',
      }}
      bodyStyle={{ flex: '1 1 auto', padding: '0 14px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div className="flex items-center px-2 pt-1">
        <div className="flex flex-col gap-[2px]">
          <span className="text-[26px] font-extrabold leading-[1.05]">Important mail</span>
          <span className="text-[13px] font-semibold text-m-sub">
            {left === 0 ? 'All handled' : `${left} need you${more > 0 ? ` · ${more} more in Gmail` : ''}`}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="m-tap-round ml-auto flex size-11 flex-none items-center justify-center rounded-full bg-m-card"
        >
          <Glyph name="close" size={16} strokeWidth={2.6} />
        </button>
      </div>

      {rows.map((r, i) => {
        const gone = handled.has(r.id)
        const isOpen = expanded === r.id && !gone
        const hot = i === 0
        return (
          <div
            key={r.id}
            className={clsx(
              'm-collapse flex-none overflow-hidden rounded-[26px]',
              hot ? 'bg-m-slab text-m-on-slab' : 'bg-m-card text-m-ink',
            )}
            style={{ maxHeight: gone ? 0 : isOpen ? 600 : 240, opacity: gone ? 0 : 1 }}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setExpanded(isOpen ? null : r.id)}
              className="flex w-full items-start gap-3 px-4 py-[15px] text-left"
            >
              <span
                className={clsx(
                  'flex size-[38px] flex-none items-center justify-center rounded-full text-[14px] font-extrabold',
                  hot ? 'bg-m-accent text-m-on-accent' : 'bg-m-chip text-m-ink',
                )}
                aria-hidden
              >
                {r.init}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="flex gap-2 text-[13px] font-bold opacity-75">
                  <span className="min-w-0 truncate">{r.from}</span>
                  <span className="ml-auto flex-none">{r.when}</span>
                </span>
                <span className="text-[16px] font-extrabold leading-[1.2]">{r.subject}</span>
                {r.why && (
                  <span className={clsx('text-[12.5px] font-bold', hot ? 'text-m-accent' : 'text-m-sub')}>{r.why}</span>
                )}
              </span>
            </button>
            {isOpen && (
              <div className="flex flex-col gap-3 pb-4 pl-[66px] pr-4">
                {r.snippet && <span className="text-[14px] leading-[1.45] opacity-85">{r.snippet}</span>}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onRemind(r)}
                    className={clsx('m-tap h-[38px] rounded-[19px] px-[14px] text-[13.5px] font-extrabold', hot ? 'bg-m-slab-wash' : 'bg-m-chip')}
                  >
                    Remind me
                  </button>
                  <button
                    type="button"
                    onClick={() => onHandle(r)}
                    className={clsx('m-tap h-[38px] rounded-[19px] px-[14px] text-[13.5px] font-extrabold', hot ? 'bg-m-slab-wash' : 'bg-m-chip')}
                  >
                    Mark handled
                  </button>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="m-tap flex h-[38px] items-center rounded-[19px] px-[14px] text-[13.5px] font-extrabold opacity-80"
                  >
                    Open in Gmail ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        )
      })}

      {left === 0 && (
        <div className="flex flex-none flex-col items-center gap-[6px] rounded-[26px] bg-m-card px-5 py-[30px] text-center">
          <span className="text-[18px] font-extrabold">Nothing important waiting</span>
          <span className="text-[13.5px] text-m-sub">New flagged mail shows up here and on Day.</span>
          {handled.size > 0 && (
            <button
              type="button"
              onClick={onUndoAll}
              className="m-tap mt-[6px] h-[38px] rounded-[19px] bg-m-chip px-4 text-[13.5px] font-extrabold"
            >
              Undo all
            </button>
          )}
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2 px-[10px] pt-2">
        {unsorted > 0 && (
          <Link to="/day" onClick={onClose} className="m-tap self-start text-[13px] font-bold text-m-ink underline underline-offset-2">
            {unsorted} not sorted yet
          </Link>
        )}
        <span className="text-[12.5px] leading-[1.45] text-m-sub">
          LifeOS only shows mail it flagged as important — from your school, advisor and anything with a
          deadline. Everything else stays in Gmail.{checked ? ` Last checked ${sinceWhen(checked, today)}.` : ''}
        </span>
        <Link to="/day" onClick={onClose} className="m-tap self-start text-[12.5px] font-bold text-m-sub underline underline-offset-2">
          All mail
        </Link>
      </div>
    </MobileSheet>
  )
}
